import { IconProp } from '@fortawesome/fontawesome-svg-core'
import { faDollarSign } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  DataTable,
  DataTableColumn,
  DataTableOptions,
  DataTableSkeleton,
  DataType,
  Form,
  FormDialogMode,
  FormOptions,
  FormSkeleton,
  RowCustomAction,
  Section,
  SelectOption,
  SortDirection,
  useNotify,
  UserPermission
} from '@globaleris/ui.library'
import {
  Box,
  Breadcrumbs,
  Button,
  Card,
  CardContent,
  CardHeader,
  Grid,
  makeStyles,
  Paper,
  Typography,
  useTheme
} from '@material-ui/core'
import { Delete, Edit, HighlightOff, Tune } from '@material-ui/icons'
import moment from 'moment'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { EnumCurrency, EnumEstadoCliente, EnumTipoContrato, EnumTipoSujeto } from '../../../api'
import { ProductSelectOption } from '../../../api/Argentia/endpoints/contracts/model/contracts'
import { EnumCommodity } from '../../../api/Argentia/enums'
import { EnumFormaPago } from '../../../api/Argentia/enums/enumFormaPago'
import { PaymentMethodTypeModel } from '../../../api/Argentia/model/contracts'
import { AccountTableFormat } from '../../../api/Argentia/model/subjects'
import { EnumTipoMetodoPago } from '../../../api/oData/enums/enumTipoMetodoPago'
import ButtonWithPermissions from '../../../components/buttonWithPermissions/buttonWithPermissions'
import { DataTableErrorHandler } from '../../../components/errors/scopes/dataTable/dataTableError'
import Page, { PageLayout } from '../../../components/page/page'
import PageHeader from '../../../components/page/pageHeader'
import { BreadcrumbLink } from '../../../components/shared/breadcrumbLink/breadcrumbLink'
import { WarningBanner } from '../../../components/shared/warningBanner/warningBanner'
import { enumEnumToValueNumber, enumToSelectOptionArray } from '../../../helpers'
import { associateActionIcon } from '../../../helpers/dataTableActionIconHelper'
import { getUserPermissions, hasPermission, hasResource } from '../../../helpers/permissionsHelper'
import {
  missingFieldsForm,
  missingFieldsFormWithRequiredText
} from '../../../helpers/validateFormHelper'
import { useConfirmationDialog, useFormDialog, useHttpError } from '../../../hooks'
import { ActionIcons, GlobalNotification, NonAuthRoutes } from '../../../model'
import { ConfirmationDialogs } from '../../../model/enums/confirmationDialogs'
import { InformationDialogs } from '../../../model/enums/informationDialogs'
import { Pages, Resources } from '../../../model/RBAC/constants'
import { SearchSelectOption } from '../../../model/searchSelectOption'
import {
  fetchClientContract,
  fetchClientContractsByID,
  fetchProductsByContract,
  getActiveCompany,
  getCustomerDomain,
  notifyError,
  setDesactiveContract,
  storeAddContractProductTemp,
  storeRemoveContractProductTemp,
  storeUpdateContractProductTemp
} from '../../../redux'
import {
  selectActiveContract,
  selectClientData,
  selectContractProductsTemp,
  selectIsFetchingContract,
  selectIsFetchingContractError,
  selectIsFetchingContractProducts,
  selectIsFetchingContractProductsError
} from '../../../redux/selectors/client.selectors'
import { getLinkClientText } from '../../clients/client.helper'
import { fetchPostalCodes, getPostalAddressByID } from '../../clients/client.service'
import { EmptyContent } from '../../clients/file/components/shared/emptyCard'
import { fetchProductsBySubjectType } from '../../products/products.service'
import {
  accountsToTableFormatMapper,
  contractModelToContractFormMapper,
  paymentMethodTypesToOptionsMapper
} from '../contracts.mapper'
import {
  AnnexedContractsFormTableModel,
  ContractEditFormValidations,
  ContractForm,
  ContractFormRaw,
  InitialAddressModel,
  NewContractProduct,
  ProductEditFormDialogValidations,
  RulebookContractEditValidations,
  RulebookContractsValidations
} from '../contracts.model'
import {
  checkDuplicateContractCode,
  checkValidIBAN,
  fetchBillingCycleBySubjectType,
  fetchBooks,
  fetchContractTypes,
  fetchHoldOptions,
  fetchPaymentMethodsTypes,
  getClientAccounts,
  updateContract
} from '../contracts.service'
import { styles } from '../contracts.styles'
import { AccountsTableDialog } from '../shared/components/accountsTable.dialog'
import { productFormDefinition } from '../shared/definitions/definitions'
import { AnnexedContractsSection } from './components/annexedContracts'
interface ContractEditPageProps {
  location: { state: { contractID: number } }
}

const initialProductValidations: ProductEditFormDialogValidations = {
  isDirty: false,
  startDate: true,
  endDate: true
}

const initialValidations: ContractEditFormValidations = {
  isDirty: false,
  startDate: true,
  endDate: true,
  signDate: true,
  contractCode: true,
  iban: true,
  postalCode: true
}

export const ContractEditPage: React.FC<ContractEditPageProps> = props => {
  const history = useHistory()
  const currentTheme = useTheme()
  const useStyles = makeStyles(styles(currentTheme))
  const classes = useStyles()

  const [t] = useTranslation(['contracts', 'globalerisLibrary', 'enums'])
  const [globalNotification, setGlobalNotification] = useState<GlobalNotification | undefined>(
    undefined
  )
  const { info } = useNotify()
  const {
    openForm,
    getFormData,
    updateFormData,
    updateFormDefinition,
    closeForm,
    formProps,
    formHookProps,
    CustomFormDialog
  } = useFormDialog()
  const { getInformation, getConfirmation, closeConfirmation, closeInformation } =
    useConfirmationDialog()
  const { errorProps } = useHttpError()

  const contractID = props.location.state.contractID

  const [contractDataForm, setContractDataForm] = useState<
    Partial<ContractFormRaw> | null | undefined
  >(undefined)
  const [annexedContracts, setAnnexedContracts] = useState<AnnexedContractsFormTableModel[]>([])

  const [pendingChanges, setPendingChanges] = useState<boolean>(false)
  const [hasModifiedDates, setHasModifiedDates] = useState<boolean>(false)
  const [isDatesWarningDismissed, setIsDatesWarningDismissed] = useState<boolean>(false)
  const [isSaving, setIsSaving] = useState<boolean>(false)
  const [fetchingProductsOps, setFetchingProductsOps] = useState<boolean>(false)
  const [productsOps, setProductsOps] = useState<ProductSelectOption[]>([])
  const [billingCycleOptions, setBillingCycleOptions] = useState<SelectOption[]>([])
  const [booksOptions, setBooksOptions] = useState<SelectOption[]>([])
  const [holdOptions, setHoldOptions] = useState<SelectOption[]>([])
  const [isFetchingHoldOptions, setIsFetchingHoldOptions] = useState<boolean>(false)
  const [isFetchingBooksOptions, setIsFetchingBooksOptions] = useState<boolean>(false)

  const [paymentMethodOptions, setPaymentMethodOptions] = useState<SelectOption[]>([])
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethodTypeModel[]>([])
  const [ibanRequired, setIbanRequired] = useState<boolean>(false)

  const [contractTypesOptions, setContractTypesOptions] = useState<SelectOption[]>([])
  const [isFetchingBillingOptions, setIsFetchingBillingOptions] = useState<boolean>(false)
  const [isFetchingPaymentOptions, setIsFetchingPaymentOptions] = useState<boolean>(false)
  const [validations, setValidations] = useState<ContractEditFormValidations>(initialValidations)
  const [validationsDialog, setValidationsDialog] =
    useState<ProductEditFormDialogValidations>(initialProductValidations)

  const [clientAccounts, setClientAccounts] = useState<AccountTableFormat[] | undefined>(undefined)
  const [showAccountsTable, setShowAccountsTable] = useState<boolean>(false)

  const clientData = useSelector(selectClientData)
  const activeContract = useSelector(selectActiveContract)
  // Memoize to prevent infinite loops - mapper creates new object on every render
  const contractData = useMemo(
    () => contractModelToContractFormMapper(activeContract),
    [activeContract]
  )
  const productsTemp = useSelector(selectContractProductsTemp)

  const [initialAddress, setInitialAddress] = useState<InitialAddressModel | null>(null)

  const isFetching = useSelector(selectIsFetchingContract)
  const isFetchingProducts = useSelector(selectIsFetchingContractProducts)
  const fetchingError = useSelector(selectIsFetchingContractError)
  const isFetchingProductsError = useSelector(selectIsFetchingContractProductsError)
  const activeCompany = getActiveCompany()?.id

  const typeOptions = enumToSelectOptionArray(EnumTipoContrato, 'enums:EnumTipoContrato')
  const statusOptions = enumToSelectOptionArray(EnumEstadoCliente, 'enums:EnumEstadoCliente')

  // Hooks Region Starts

  const productsActions = (prod: NewContractProduct[] | null | undefined): any => {
    if (prod) {
      return prod.map(p => {
        const actionIcon = p.tempId
          ? associateActionIcon(p, ActionIcons.NEW_ITEM, 'No Guardado')
          : undefined
        return { ...p, actions: actionIcon }
      })
    }
    return prod
  }

  const products = productsActions(productsTemp)

  const getBillingCycle = async (clientType: EnumTipoSujeto) => {
    setIsFetchingBillingOptions(true)
    await fetchBillingCycleBySubjectType(clientType).then(response => {
      if (response.data !== undefined) {
        setBillingCycleOptions(response.data)
      }
      setIsFetchingBillingOptions(false)
    })
  }

  const getContractTypes = async (clientType: EnumTipoSujeto, commodityType: EnumCommodity) => {
    await fetchContractTypes(clientType, commodityType).then(response => {
      if (response.data !== undefined) {
        setContractTypesOptions(response.data)
      }
    })
  }

  const getPaymentMethods = async () => {
    setIsFetchingPaymentOptions(true)

    await fetchPaymentMethodsTypes().then(response => {
      if (response.data !== undefined) {
        setPaymentMethodOptions(paymentMethodTypesToOptionsMapper(response.data))
        setPaymentMethods(response.data)
      }
      setIsFetchingPaymentOptions(false)
    })
  }

  const getBooks = async () => {
    setIsFetchingBooksOptions(true)
    await fetchBooks().then(response => {
      setIsFetchingBooksOptions(false)
      if (response !== undefined) {
        setBooksOptions(response)
      }
    })
  }

  const getHoldOptions = async () => {
    setIsFetchingHoldOptions(true)

    await fetchHoldOptions().then(response => {
      if (response.data !== undefined) {
        setHoldOptions(response.data)
      }
      setIsFetchingHoldOptions(false)
    })
  }

  const getProducts = async (clientType: EnumTipoSujeto, commodity: EnumCommodity) => {
    await fetchProductsBySubjectType(clientType, commodity).then(response => {
      if (response.data !== undefined) {
        setProductsOps(response.data)
      }
    })
  }

  useEffect(() => {
    return () => {
      setDesactiveContract()
    }
  }, [])

  useEffect(() => {
    setFetchingProductsOps(true)

    let clientType = clientData && clientData?.type ? clientData?.type : EnumTipoSujeto.ClienteFinal

    getBillingCycle(clientType)
    getPaymentMethods()
    getBooks()
    getHoldOptions()

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientData])

  // Fetch contract data when contractID or activeCompany changes
  useEffect(() => {
    if (contractID) {
      fetchClientContract(contractID)
      fetchProductsByContract(contractID)
    }
  }, [contractID, activeCompany])

  // Update form when contract data is loaded or fetch completes
  // isFetching triggers update after full data arrives (fixes missing fields from partial loads)
  useEffect(() => {
    if (contractData && contractData.id === contractID) {
      setContractDataForm({ ...contractData } as any)
      setInitialAddress({
        countryId: contractData.countryId!,
        country: contractData.country,
        provinceId: contractData.provinceId!,
        province: contractData.province,
        municipalityId: contractData.municipalityId!,
        municipality: contractData.municipality,
        postalCode: contractData.postalCode ?? '',
        postalAddress: contractData.postalAddress ?? '',
        postalCodeSearch: {
          value: contractData.postalCode!,
          label: contractData.postalCode!,
          textValue: contractData.postalCode!
        }
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contractData?.id, contractID, isFetching])

  // Cannot be on any of the other effects because we need both contractData and clientData for this fetch
  useEffect(() => {
    if (contractData?.commodity && clientData?.type) {
      setFetchingProductsOps(true)
      getProducts(clientData.type, contractData.commodity).then(responses => {
        setFetchingProductsOps(false)
      })
      getContractTypes(clientData?.type, contractData?.commodity)
    }
  }, [clientData?.type, contractData?.commodity])

  const handleFormDirty = (
    validation: RulebookContractsValidations | RulebookContractEditValidations,
    valid: boolean,
    cause?: string
  ) => {
    setValidations(prevValidations => {
      const newValidation = { ...prevValidations, [validation]: valid }
      const isDirty = !(
        newValidation.startDate &&
        newValidation.endDate &&
        newValidation.signDate &&
        newValidation.contractCode &&
        newValidation.iban &&
        newValidation.postalCode
      )
      return { ...newValidation, isDirty: isDirty }
    })
  }

  const handleFormDirtyDialog = (validation: 'startDate' | 'endDate', valid: boolean) => {
    setValidationsDialog(prevValidations => {
      const newValidation = { ...prevValidations, [validation]: valid }
      const isDirty = !(newValidation.startDate && newValidation.endDate)
      return { ...newValidation, isDirty: isDirty }
    })
  }

  const validateStartDate = (startDate?: Date, endDate?: Date | null) => {
    if (endDate && startDate && moment(endDate).isBefore(moment(startDate), 'days')) {
      handleFormDirty(RulebookContractsValidations.START_DATE, false)
      return
    } else {
      handleFormDirty(RulebookContractsValidations.START_DATE, true)
    }
  }

  const validateEndDate = (startDate?: Date, endDate?: Date | null) => {
    if (startDate && endDate && moment(endDate).isBefore(moment(startDate), 'days')) {
      handleFormDirty(RulebookContractsValidations.END_DATE, false)
    } else {
      handleFormDirty(RulebookContractsValidations.END_DATE, true)
    }
  }

  const validateSignDate = (signDate?: Date, startDate?: Date) => {
    if (signDate && startDate && moment(signDate).isAfter(moment(startDate), 'days')) {
      handleFormDirty(RulebookContractsValidations.SIGN_DATE, false)
    } else {
      handleFormDirty(RulebookContractsValidations.SIGN_DATE, true)
    }
  }

  const validateStartDateDialog = (startDate?: Date, endDate?: Date) => {
    if (endDate && startDate && moment(endDate).isBefore(moment(startDate), 'days')) {
      handleFormDirtyDialog(RulebookContractsValidations.START_DATE, false)
      return
    } else {
      handleFormDirtyDialog(RulebookContractsValidations.START_DATE, true)
      handleFormDirtyDialog(RulebookContractsValidations.END_DATE, true)
    }
  }

  const validateEndDateDialog = (startDate?: Date, endDate?: Date) => {
    if (startDate && endDate && moment(endDate).isBefore(moment(startDate), 'days')) {
      handleFormDirtyDialog(RulebookContractsValidations.END_DATE, false)
    } else {
      handleFormDirtyDialog(RulebookContractsValidations.START_DATE, true)
      handleFormDirtyDialog(RulebookContractsValidations.END_DATE, true)
    }
  }

  const isSameDate = (date?: Date | string | null, originalDate?: Date | string | null) => {
    if (!date && !originalDate) return true
    if (!date || !originalDate) return false
    return moment(date).isSame(moment(originalDate), 'day')
  }

  const checkModifiedDates = (startDate?: Date | null, endDate?: Date | null) => {
    setIsDatesWarningDismissed(false)
    setHasModifiedDates(
      !isSameDate(startDate, contractData?.startDate) || !isSameDate(endDate, contractData?.endDate)
    )
  }

  const handleNewIban = async (iban: string) => {
    const response = await checkValidIBAN(iban)
    if (response.success && response.data) {
      if (response.data.isValid) {
        handleFormDirty('iban', true)
      } else {
        handleFormDirty('iban', false)
      }
    }
  }

  const handleNewCode = async (code: string) => {
    const response = await checkDuplicateContractCode(code, contractID)
    if (response.success && response.data) {
      if (response.data.isValid) {
        handleFormDirty('contractCode', true)
      } else {
        handleFormDirty('contractCode', false)
      }
    }
  }

  const handleCancelClick = async () => {
    if (pendingChanges) {
      await getConfirmation(ConfirmationDialogs.CANCEL_PENDING_CHANGES).then(value => {
        history.goBack()
      })
      closeConfirmation()
    } else {
      history.goBack()
    }
  }

  const handlePaymentMethodChange = (paymentMethodId: number) => {
    const paymentMethod = paymentMethods.find(paymentMethod => paymentMethod.Id === paymentMethodId)

    if (paymentMethod && paymentMethod.methodType === EnumFormaPago.Domiciliacion) {
      setIbanRequired(true)
    } else {
      ibanRequired && setIbanRequired(false)
    }
  }

  const onUpdateContract = async () => {
    let resp = {
      success: false,
      contractID: 0
    }
    if (contractDataForm && contractData && clientData) {
      if (missingFieldsForm(contractSection, contractDataForm)) {
        notifyError(t('contracts:notifications.create.errorInvalidForm'))
      } else {
        setIsSaving(true)
        await updateContract(
          contractID,
          { ...(contractDataForm as ContractFormRaw), products: products ?? [] },
          contractData as ContractForm
        ).then(response => {
          if (response.success) {
            info(t('contracts:notifications.edition.success'))
            clientData?.id && fetchClientContractsByID(clientData.id)
            history.goBack()
          } else {
            notifyError(t('contracts:notifications.edition.error'))
          }
          setIsSaving(false)
        })
      }
    }
    return resp
  }

  // Hooks Region Ends

  //Functions Region Starts

  const handleShowExistingAccounts = async () => {
    if (clientData?.id) {
      await getClientAccounts(clientData?.id).then(response => {
        if (response.success) {
          setClientAccounts(accountsToTableFormatMapper(response.data ?? []))
          setShowAccountsTable(true)
        } else {
          notifyError(t('contracts:notifications.fetchAccounts.error'))
        }
      })
    }
  }

  const handleClickAccountsTable = (account: AccountTableFormat | null) => {
    setContractDataForm(prevState => ({ ...prevState, useExistingAccount: false }))
    setPendingChanges(true)
    if (account) {
      setContractDataForm(prevState => ({
        ...prevState,
        paymentMethod: enumEnumToValueNumber(EnumTipoMetodoPago, account.paymentMethod.type),
        iban: account.paymentMethod.iban,
        billingCycle: account.billingCycleId,
        municipality: account.paymentMethod.address.municipality,
        province: account.paymentMethod.address.province,
        country: account.paymentMethod.address.country,
        municipalityId: account.paymentMethod.address.municipalityId,
        provinceId: account.paymentMethod.address.provinceId,
        countryId: account.paymentMethod.address.countryId,
        postalCode: account.paymentMethod.address.postalCode,
        postalCodeSearch: {
          value: account.paymentMethod.address.postalCode,
          label: account.paymentMethod.address.postalCode,
          textValue: account.paymentMethod.address.postalCode
        },
        postalAddress: account.paymentMethod.address.postalAddress
      }))
    }
    setShowAccountsTable(false)
  }

  const updateAddress = async (option: SearchSelectOption | null) => {
    if (!option) {
      setContractDataForm(prevState => ({
        ...prevState,
        municipalityId: undefined,
        municipality: '',
        provinceId: undefined,
        province: '',
        countryId: undefined,
        country: ''
        // postalCode: ''
      }))
      return
    }

    setPendingChanges(true)
    const postalAddress = await getPostalAddressByID(+option.value).catch(e => {})

    if (postalAddress) {
      setContractDataForm(prevState => ({
        ...prevState,
        municipalityId: postalAddress.municipality.id,
        municipality: postalAddress.municipality.description,
        provinceId: postalAddress.province.id,
        province: postalAddress.province.description,
        countryId: postalAddress.country.id,
        country: postalAddress.country.description
      }))
    }
  }

  //Functions Region Ends

  //Form config Starts
  const options: FormOptions = useMemo(() => {
    return {
      columns: 3,
      analytics: {
        customer: getCustomerDomain(),
        view: t('contracts:forms.edit.analytics')
      }
    }
  }, [t, getCustomerDomain])

  const contractSection: Section[] = [
    {
      title: t('contracts:forms.edit.titles.contract'),
      inputs: [
        {
          name: 'type',
          label: t('contracts:forms.add.inputs.contractType'),
          selectOptions: contractTypesOptions,
          type: DataType.SELECT,
          required: true,
          onChange: value => {
            setContractDataForm(prevState => ({ ...prevState, type: value }))
            setPendingChanges(true)
          }
        },
        {
          name: 'contractCode',
          label: t('contracts:forms.edit.inputs.contractCode'),
          type: DataType.TEXT,
          required: true,
          errorState: !validations.contractCode,
          errorText: t('contracts:notifications.errors.contractCode'),
          onBlur: code => {
            handleNewCode(`${code}`)
            setContractDataForm(prevState => ({ ...prevState, contractCode: `${code}` }))
            setPendingChanges(true)
          }
        },
        {
          name: 'startDate',
          label: t('contracts:forms.edit.inputs.startDate'),
          type: DataType.DATE,
          required: true,
          errorState: !validations.startDate,
          errorText: t('contracts:notifications.errors.startDate'),
          onBlur: (newStartDate: any) => {
            setPendingChanges(true)
            validateStartDate(newStartDate, contractDataForm?.endDate)
            validateEndDate(newStartDate, contractDataForm?.endDate)
            validateSignDate(contractDataForm?.signatureDate, newStartDate)
            checkModifiedDates(newStartDate, contractDataForm?.endDate)
          }
        },
        {
          name: 'endDate',
          label: t('contracts:forms.edit.inputs.endDate'),
          type: DataType.DATE,
          errorState: !validations.endDate,
          errorText: t('contracts:notifications.errors.endDate'),
          onBlur: (newEndDate: any) => {
            setPendingChanges(true)
            validateStartDate(contractDataForm?.startDate, newEndDate)
            validateEndDate(contractDataForm?.startDate, newEndDate)
            checkModifiedDates(contractDataForm?.startDate, newEndDate)
          }
        },
        {
          name: 'signatureDate',
          label: t('contracts:forms.edit.inputs.signDate'),
          type: DataType.DATE,
          required: true,
          errorState: !validations.signDate,
          errorText: t('contracts:notifications.errors.signDate'),
          onBlur: (value: any) => {
            setPendingChanges(true)
            validateSignDate(value, contractDataForm?.startDate)
          }
        },
        {
          name: 'state',
          label: t('contracts:forms.edit.inputs.status'),
          type: DataType.SELECT,
          selectOptions: statusOptions ?? [],
          onChange: value => setPendingChanges(true)
        },
        {
          name: 'expectedStartDate',
          label: t('contracts:forms.edit.inputs.expectedStartDate'),
          type: DataType.DATE,
          readOnly: true
        },
        {
          name: 'expectedEndDate',
          label: t('contracts:forms.edit.inputs.expectedEndDate'),
          type: DataType.DATE,
          readOnly: true
        }
      ]
    },
    {
      title: t('contracts:forms.edit.titles.account'),
      inputs: [
        {
          name: 'billingCycleId',
          label: t('contracts:forms.edit.inputs.billingCycle'),
          type: DataType.SELECT,
          selectOptions: billingCycleOptions ?? [],
          isLoading: isFetchingBillingOptions,
          required: true,
          onChange: value => setPendingChanges(true)
        },
        {
          name: 'paymentMethodType',
          label: t('contracts:forms.edit.inputs.paymentMethod'),
          type: DataType.SELECT,
          selectOptions: paymentMethodOptions ?? [],
          isLoading: isFetchingPaymentOptions,
          required: true,
          onChange: newMethod => {
            setPendingChanges(true)
            handlePaymentMethodChange(+newMethod)
          }
        },
        {
          name: 'iban',
          label: t('contracts:forms.edit.inputs.iban'),
          type: DataType.TEXT,
          required: ibanRequired,
          errorState: !validations.iban,
          errorText: t('contracts:notifications.errors.iban'),
          onBlur: iban => {
            setPendingChanges(true)
            handleNewIban(`${iban}`)
            setContractDataForm(prevState => ({ ...prevState, iban: `${iban}` }))
          }
        },
        {
          name: 'accountCode',
          label: t('contracts:forms.edit.inputs.accountCode'),
          type: DataType.TEXT,
          required: true,
          onBlur: code => {
            setPendingChanges(true)
            setContractDataForm(prevState => ({ ...prevState, accountCode: `${code}` }))
          }
        },
        {
          name: 'postalAddress',
          label: t('contracts:forms.edit.inputs.street'),
          type: DataType.TEXT,
          required: true,
          onBlur: address => {
            setPendingChanges(true)
            setContractDataForm(prevState => ({ ...prevState, postalAddress: `${address}` }))
          }
        },
        {
          name: 'postalCodeSearch',
          label: t('contracts:forms.edit.inputs.postalCode'),
          type: DataType.SEARCHFIELD,
          apiCall: fetchPostalCodes,
          defaultValue: { value: initialAddress?.postalCode, label: initialAddress?.postalCode },
          maxLength: 5,
          required: true,
          clearable: true,
          onChange: newCode => {
            const option = newCode as any
            updateAddress(option as SearchSelectOption | null)
          }
        },
        {
          name: 'municipality',
          label: t('contracts:forms.edit.inputs.municipality'),
          type: DataType.TEXT,
          required: true,
          readOnly: true
        },
        {
          name: 'province',
          label: t('contracts:forms.edit.inputs.province'),
          type: DataType.TEXT,
          required: true,
          readOnly: true
        },
        {
          name: 'country',
          label: t('contracts:forms.edit.inputs.country'),
          type: DataType.TEXT,
          required: true,
          readOnly: true
        },
        {
          name: 'useExistingAccount',
          label: t('contracts:forms.add.inputs.useExistingAccount'),
          type: DataType.SWITCH,
          readOnly: !clientData?.id,
          onChange: value => {
            if (value) {
              handleShowExistingAccounts()
            }
          }
        }
      ]
    },
    {
      title: t('contracts:forms.add.titles.aditionals'),
      inputs: [
        {
          name: 'description',
          label: t('contracts:forms.add.inputs.contractDescription'),
          type: DataType.TEXT,
          onChange: value => {
            setContractDataForm(prevState => ({
              ...prevState,
              description: value.toString()
            }))
            setPendingChanges(true)
          }
        },
        {
          name: 'bookID',
          label: t('contracts:forms.add.inputs.book'),
          type: DataType.SELECT,
          isLoading: isFetchingBooksOptions,
          selectOptions: booksOptions,
          clearable: true,
          onChange: value => setPendingChanges(true)
        },
        {
          name: 'hold',
          label: t('contracts:forms.add.inputs.hold'),
          type: DataType.SELECT,
          isLoading: isFetchingHoldOptions,
          selectOptions: holdOptions,
          clearable: true,
          onChange: value => setPendingChanges(true)
        }
        // {
        //   name: 'orderNumber',
        //   label: t('contracts:forms.add.inputs.orderNumber'),
        //   type: DataType.TEXT
        // }
      ],
      collapsible: true
    }
  ]

  const prodFormDefinition = useCallback(
    (mode: 'ADD' | 'EDIT' | 'DELETE') =>
      productFormDefinition(mode, {
        t,
        productsOps,
        validationsDialog,
        validateStartDateDialog,
        validateEndDateDialog,
        minStartDate: contractDataForm?.startDate,
        maxEndDate: contractDataForm?.endDate,
        fetchingProductsOps
      }),
    [
      t,
      productsOps,
      validationsDialog,
      validateStartDateDialog,
      validateEndDateDialog,
      contractDataForm?.startDate,
      contractDataForm?.endDate,
      fetchingProductsOps
    ]
  )

  const productColumns: DataTableColumn[] = [
    {
      name: 'actions',
      label: '',
      hideForResponsive: true,
      type: DataType.HTML,
      columnWidth: 65
    },
    {
      name: 'productId',
      type: DataType.NUMBER,
      display: false
    },
    {
      name: 'productCode',
      label: t('contracts:components.productsTable.columns.code'),
      type: DataType.TEXT
    },
    {
      name: 'productDescription',
      label: t('contracts:components.productsTable.columns.description'),
      type: DataType.TEXT
    },
    {
      name: 'startDate',
      label: t('contracts:components.productsTable.columns.startDate'),
      type: DataType.DATE
    },
    {
      name: 'endDate',
      label: t('contracts:components.productsTable.columns.endDate'),
      type: DataType.DATE
    },
    {
      name: 'currency',
      label: t('contracts:components.productsTable.columns.currency'),
      type: DataType.SELECT,
      selectOptions: enumToSelectOptionArray(EnumCurrency, 'enums:EnumCurrencyLabel'),
      display: enumEnumToValueNumber(EnumTipoSujeto, clientData?.type) === EnumTipoSujeto.Mayorista
    }
  ]

  const onAddProductClickHandler = () => {
    openForm({
      definition: prodFormDefinition('ADD'),
      title: t('contracts:components.productsTable.actions.add.title'),
      okBtnLabel: t('contracts:components.productsTable.actions.add.okBtnLabel'),
      mode: FormDialogMode.EDIT,
      isWaiting: false,
      inputData: {
        startDate: contractDataForm?.startDate ?? new Date()
      } as NewContractProduct,
      onClose: formData => {
        if (formData) {
          formData.currency = productsOps.find(p => p.value === formData.productId)?.currency

          if (missingFieldsFormWithRequiredText(prodFormDefinition('ADD').sections!, formData)) {
            notifyError(t('contracts:notifications.create.errorInvalidForm'))
            return
          }
          getInformationSave(() => onCloseAddForm(formData as NewContractProduct))
        } else {
          closeForm()
        }
      }
    })
  }

  const onEditProductClickHandler = (product: NewContractProduct) => {
    openForm({
      definition: prodFormDefinition('EDIT'),
      title: t('contracts:components.productsTable.actions.edit.title'),
      okBtnLabel: t('contracts:components.productsTable.actions.edit.okBtnLabel'),
      mode: FormDialogMode.EDIT,
      isWaiting: false,
      onClose: formData => {
        formData
          ? getInformationSave(() => onCloseEditForm(formData as NewContractProduct))
          : closeForm()
      },
      inputData: product
    })
  }

  // "Remove" a save product is actually editing its endDate.
  const onRemoveProductClickHandler = (product: NewContractProduct) => {
    openForm({
      definition: prodFormDefinition('DELETE'),
      title: t('contracts:components.productsTable.actions.edit.title'),
      mode: FormDialogMode.EDIT,
      isWaiting: false,
      onClose: (formData: Partial<NewContractProduct>) => {
        formData
          ? getInformationSave(() => onCloseRemoveForm({ ...product, ...formData }))
          : closeForm()
      },
      inputData: product
    })
  }

  const onCloseAddForm = (formData: NewContractProduct) => {
    if (formData) {
      storeAddContractProductTemp({
        ...formData,
        productDescription: productsOps.find(p => p.value === formData.productId)?.label,
        productCode: productsOps.find(p => p.value === formData.productId)?.code
      })
    }
    setValidationsDialog({ isDirty: false, startDate: true, endDate: true })
    closeForm()
  }

  const onCloseEditForm = (product: NewContractProduct) => {
    if (product) {
      storeUpdateContractProductTemp({
        ...product,
        productDescription: productsOps.find(p => p.value === product.productId)?.label,
        productCode: productsOps.find(p => p.value === product.productId)?.code
      })
    }
    setValidationsDialog({ isDirty: false, startDate: true, endDate: true })
    closeForm()
  }

  const onCloseRemoveForm = (product: NewContractProduct) => {
    if (product) {
      storeUpdateContractProductTemp(product)
    }
    closeForm()
  }

  const onDeleteProductClickHandler = (product: Partial<NewContractProduct>) => {
    if (product) {
      if (products.length > 1) {
        getInformationSave(() => {
          product.id
            ? storeRemoveContractProductTemp(product.id)
            : product.tempId && storeRemoveContractProductTemp(product.tempId)
        })
      } else {
        getInformationPreventFromDelete()
      }
    }
  }

  const onViewQuotesClickHandler = (rowData: NewContractProduct) => {
    history.push(NonAuthRoutes.QuotesProducts, {
      productData: rowData,
      contractData: contractData
    })
  }

  const onCustomizeProductClickHandler = (rowData: NewContractProduct) => {
    history.push(NonAuthRoutes.ProductsCustomize, {
      productContractId: rowData.id,
      productContractStartDate: rowData.startDate,
      productContractEndDate: rowData.endDate,
      productId: rowData.productId,
      clientType: enumEnumToValueNumber(EnumTipoSujeto, clientData?.type),
      contractData: {
        id: contractData?.id,
        code: contractData?.contractCode,
        description: contractData?.description,
        commodity: contractData?.commodity
      }
    })
  }

  const getInformationPreventFromDelete = async () => {
    await getInformation(InformationDialogs.PREVENT_FROM_DELETE_LAST_PRODUCT_CONTRACT).then(() => {
      closeConfirmation()
    })
  }
  const getInformationSave = async (callback: () => void) => {
    await getInformation(InformationDialogs.PENDING_SAVE).then(res => {
      setPendingChanges(true)
      closeInformation()
      return callback()
    })
  }

  const getRowCustomActions = () => {
    const actions: RowCustomAction[] = []

    if (hasResource(Resources.Leads)) {
      actions.push({
        userPermission: UserPermission.EDIT,
        icon: <FontAwesomeIcon size="sm" icon={faDollarSign as IconProp} />,
        tooltip: t('contracts:components.productsTable.actions.viewQuotes.title'),
        onClick: rowData => onViewQuotesClickHandler(rowData)
      })
    }

    actions.push(
      {
        userPermission: UserPermission.EDIT,
        icon: <Tune fontSize="small" />,
        tooltip: t('contracts:components.productsTable.actions.customize.title'),
        onClick: (product: NewContractProduct) => onCustomizeProductClickHandler(product)
      },
      {
        userPermission: UserPermission.EDIT,
        icon: <Edit fontSize="small" />,
        tooltip: t('contracts:components.productsTable.actions.edit.title'),
        onClick: (product: NewContractProduct) => onEditProductClickHandler(product)
      },
      {
        userPermission: UserPermission.EDIT,
        icon: <HighlightOff fontSize="small" />,
        tooltip: t('contracts:components.productsTable.actions.remove.title'),
        onClick: onRemoveProductClickHandler
      },
      {
        userPermission: UserPermission.EDIT,
        icon: <Delete fontSize="small" />,
        tooltip: t('contracts:components.productsTable.actions.delete.title'),
        onClick: onDeleteProductClickHandler
      }
    )

    return actions
  }
  const tableOptions: DataTableOptions = {
    i18n: t('globalerisLibrary:dataTable', { returnObjects: true }),
    search: true,
    elevation: 0,
    customInsertEditModal: true,
    sortOrder: { name: 'endDate', direction: SortDirection.ASC },
    analytics: {
      customer: getCustomerDomain(),
      view: t('contracts:components.productsTable.analytics')
    },
    canDelete: false,
    canEdit: false,
    canInsert: hasPermission(Resources.Client, [UserPermission.EDIT]),
    onRowInsert: async () => {
      onAddProductClickHandler()
      return true
    },
    fabDisabled: !contractData?.cupsId && contractData?.type === EnumTipoContrato.ClienteFinal,
    fabDisabledTooltipLabel: t('contracts:components.productsTable.fabDisabledTooltip'),
    fabLabel: t('contracts:components.productsTable.fab'),
    rowCustomActions: getRowCustomActions()
  }

  if (!contractID) {
    history.goBack()
  }

  useEffect(() => {
    const def = formHookProps?.definition?.id
      ? prodFormDefinition(formHookProps.definition.id as any)
      : null
    def && updateFormDefinition(def)
  }, [validationsDialog])

  //Form config Ends

  return (
    <Page
      notification={globalNotification}
      fullWidth={false}
      layout={PageLayout.SINGLE_COLUMN}
      error={errorProps}
    >
      <PageHeader title={t('contracts:titleEdit')}>
        <Button
          variant="text"
          color="primary"
          size="small"
          disabled={isFetching || isSaving}
          onClick={handleCancelClick}
        >
          {t('contracts:buttonsAdd.cancel')}
        </Button>
        <ButtonWithPermissions
          variant="contained"
          color="primary"
          size="small"
          disabled={isFetching || isSaving || validations.isDirty || !pendingChanges}
          hasPermission={hasPermission(Resources.Client, [UserPermission.EDIT])}
          onClick={onUpdateContract}
        >
          {t('contracts:buttonsAdd.save')}
        </ButtonWithPermissions>
      </PageHeader>
      <Box mb={1}>
        <Breadcrumbs separator=">" aria-label="breadcrumb">
          {clientData && (
            <BreadcrumbLink
              toPathName="/client/file"
              state={{ clientID: clientData?.id }}
              confirmation={pendingChanges}
            >
              {`Cliente ${getLinkClientText(clientData)} - ID ${clientData.documentID}`}
            </BreadcrumbLink>
          )}
          <Typography key="3" color="primary" variant="subtitle1">
            Contrato {contractData?.contractCode}
          </Typography>
        </Breadcrumbs>
      </Box>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <WarningBanner
            show={hasModifiedDates && !isDatesWarningDismissed}
            message={t('contracts:notifications.edition.datesChangedWarning')}
            onClose={() => setIsDatesWarningDismissed(true)}
          />
          {!isFetching && contractDataForm ? (
            <Paper elevation={6}>
              <Form
                title={t('contracts:formTitle')}
                sections={contractSection}
                data={contractDataForm}
                options={options}
              />
              <AnnexedContractsSection
                data={contractData?.annexedContracts}
                contractId={contractData?.id!}
                commodity={contractData?.commodity}
                country={contractData?.countryId}
              />
            </Paper>
          ) : (
            <>
              <FormSkeleton columns={3} inputs={6} elevation={2} withTitle={true} />
              <FormSkeleton columns={3} inputs={9} elevation={2} withTitle={true} />
            </>
          )}
        </Grid>
        <Grid item xs={12}>
          <Paper elevation={6}>
            {isFetchingProducts ? (
              <DataTableSkeleton rows={3} elevation={2} />
            ) : products && products.length === 0 ? (
              <Card raised>
                <CardHeader title={t('contracts:components.productsTable.title')}></CardHeader>
                <EmptyContent title={t('contracts:components.productsTable.emptyCardMsg')}>
                  <ButtonWithPermissions
                    size="small"
                    color="primary"
                    variant="contained"
                    disabled={
                      !contractData?.cupsId && contractData?.type === EnumTipoContrato.ClienteFinal
                    }
                    onClick={onAddProductClickHandler}
                    hasPermission={hasPermission(Resources.Client, [UserPermission.EDIT])}
                  >
                    {t('contracts:components.productsTable.emptyCardBtn')}
                  </ButtonWithPermissions>
                </EmptyContent>
              </Card>
            ) : (
              <div style={{ marginLeft: 'auto', marginRight: 'auto' }}>
                <Card>
                  <CardHeader title={t('contracts:components.productsTable.title')}></CardHeader>
                  <CardContent>
                    <DataTable
                      data={products ?? []}
                      columns={productColumns}
                      options={tableOptions}
                      info={DataTableErrorHandler({
                        errorCode: isFetchingProductsError ? true : undefined,
                        onRefresh: async () => {
                          contractID && fetchProductsByContract(contractID)
                        }
                      })}
                      userPermissions={getUserPermissions(Pages.Client)}
                    />
                  </CardContent>
                </Card>
              </div>
            )}
          </Paper>
        </Grid>
      </Grid>
      <AccountsTableDialog
        show={showAccountsTable}
        accounts={clientAccounts}
        onHandleClick={handleClickAccountsTable}
        paymentMethodOptions={paymentMethodOptions}
        billingCycleOptions={billingCycleOptions}
      />
      <CustomFormDialog
        maxWidth={formHookProps?.definition?.id === 'DELETE' ? 'xs' : undefined}
        {...formProps}
      ></CustomFormDialog>
    </Page>
  )
}
